import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetAutoEnrollmentStatusListParams,
  GetAutoEnrollmentStatusListResponse,
  GetAWRListParams,
  GetAWRListResponse,
  GetAttachmentOrdersReportListParams,
  GetAttachmentOrdersReportListResponse,
  GetBankFilesReportParams,
  GetBankFilesReportResponse,
  GetOnHoldWorkerReportListParams,
  GetOnHoldWorkerReportListResponse,
  GetCISStateOfEarningsListParams,
  GetCISStateOfEarningsListResponse,
  GetDormantWorkerListParams,
  GetDormantWorkerListResponse,
  GetDraftPayrollListParams,
  GetDraftPayrollListResponse,
  GetEmploymentIntermediaryListParams,
  GetEmploymentIntermediaryListResponse,
  GetFiftyTwoWeekAverageSummaryListParams,
  GetFiftyTwoWeekAverageSummaryListResponse,
  GetHolidayPayBalanceDetailListParams,
  GetHolidayPayBalanceDetailListResponse,
  GetHolidayPayBalanceSummaryListParams,
  GetHolidayPayBalanceSummaryListResponse,
  GetNetPayListParams,
  GetNetPayListResponse,
  GetP30ReportParams,
  GetP30ReportResponse,
  GetP32ReportParams,
  GetP32ReportResponse,
  GetPayrollListParams,
  GetPayrollListResponse,
  GetPendingPensionContributionReportListParams,
  GetPendingPensionContributionReportListResponse,
  GetPensionContributionReportListParams,
  GetPensionContributionReportListResponse,
  GetRTWListParams,
  GetRTWListResponse,
  GetWTRListParams,
  GetWTRListResponse,
} from '../types';

export const getDraftPayrollList = createAsyncThunk<
  GetDraftPayrollListResponse,
  GetDraftPayrollListParams
>(
  'report/getDraftPayrollList',
  async (params: GetDraftPayrollListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.report.payroll.draft, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPayrollList = createAsyncThunk<
  GetPayrollListResponse,
  GetPayrollListParams
>(
  'report/getPayrollList',
  async (params: GetPayrollListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.report.payroll.mainV2, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getHolidayPayBalanceDetailList = createAsyncThunk<
  GetHolidayPayBalanceDetailListResponse,
  GetHolidayPayBalanceDetailListParams
>(
  'report/getHolidayPayBalanceDetailList',
  async (params: GetHolidayPayBalanceDetailListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.payroll.holidayPay, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getHolidayPayBalanceSummaryList = createAsyncThunk<
  GetHolidayPayBalanceSummaryListResponse,
  GetHolidayPayBalanceSummaryListParams
>(
  'report/getHolidayPayBalanceSummaryList',
  async (
    params: GetHolidayPayBalanceSummaryListParams,
    { rejectWithValue },
  ) => {
    try {
      const response = await jwtAxios.get(
        endPoints.report.payroll.holidayPaySummary,
        {
          params,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getNetPayList = createAsyncThunk<
  GetNetPayListResponse,
  GetNetPayListParams
>(
  'report/getNetPayList',
  async (params: GetNetPayListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.payroll.netPay, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getFiftyTwoWeekAverageSummaryList = createAsyncThunk<
  GetFiftyTwoWeekAverageSummaryListResponse,
  GetFiftyTwoWeekAverageSummaryListParams
>(
  'report/getFiftyTwoWeekAverageSummaryList',
  async (
    params: GetFiftyTwoWeekAverageSummaryListParams,
    { rejectWithValue },
  ) => {
    try {
      const response = await jwtAxios.get(
        endPoints.report.payroll.fiftyTwoWeekAverageSummary,
        {
          params,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getRTWList = createAsyncThunk<
  GetRTWListResponse,
  GetRTWListParams
>(
  'report/getRTWList',
  async (params: GetRTWListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.report.employee.rtw, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAWRList = createAsyncThunk<
  GetAWRListResponse,
  GetAWRListParams
>(
  'report/getAWRList',
  async (params: GetAWRListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.report.employee.awr, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getWTRList = createAsyncThunk<
  GetWTRListResponse,
  GetWTRListParams
>(
  'report/getWTRList',
  async (params: GetWTRListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.report.employee.wtr, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getCISStateOfEarningsList = createAsyncThunk<
  GetCISStateOfEarningsListResponse,
  GetCISStateOfEarningsListParams
>(
  'report/getCISStateOfEarningsList',
  async (params: GetCISStateOfEarningsListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.report.management.earning, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getDormantWorkerList = createAsyncThunk<
  GetDormantWorkerListResponse,
  GetDormantWorkerListParams
>(
  'report/getDormantWorkerList',
  async (params: GetDormantWorkerListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        endPoints.report.management.dormantWorker,
        {
          params,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getEmploymentIntermediaryList = createAsyncThunk<
  GetEmploymentIntermediaryListResponse,
  GetEmploymentIntermediaryListParams
>(
  'report/getEmploymentIntermediaryList',
  async (params: GetEmploymentIntermediaryListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        endPoints.report.management.intermediary,
        {
          params,
        },
      );
      return {
        data: response.data.data,
        agency: response.data.agency,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getEmploymentIntermediaryInAgencyPortalList = createAsyncThunk<
  GetEmploymentIntermediaryListResponse,
  GetEmploymentIntermediaryListParams
>(
  'report/getEmploymentIntermediaryInAgencyPortalList',
  async (params: GetEmploymentIntermediaryListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        endPoints.report.management.intermediaryInAgencyPortal,
        {
          params,
        },
      );
      return {
        data: response.data.data,
        agency: response.data.agency,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAutoEnrollmentStatusList = createAsyncThunk<
  GetAutoEnrollmentStatusListResponse,
  GetAutoEnrollmentStatusListParams
>(
  'report/getAutoEnrollmentStatusList',
  async (params: GetAutoEnrollmentStatusListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        endPoints.report.pension.getAutoEnrollmentStatus,
        {
          params,
        },
      );
      return {
        data: response.data.data,
        agency: response.data.agency,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPensionContributionReportList = createAsyncThunk<
  GetPensionContributionReportListResponse,
  GetPensionContributionReportListParams
>(
  'report/getPensionContributionReportList',
  async (
    params: GetPensionContributionReportListParams,
    { rejectWithValue },
  ) => {
    try {
      const response = await jwtAxios.get(
        endPoints.report.pension.contributions,
        {
          params,
        },
      );
      return {
        data: response.data.data,
        agency: response.data.agency,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPendingPensionContributionReportList = createAsyncThunk<
  GetPendingPensionContributionReportListResponse,
  GetPendingPensionContributionReportListParams
>(
  'report/getPendingPensionContributionReportList',
  async (
    params: GetPendingPensionContributionReportListParams,
    { rejectWithValue },
  ) => {
    try {
      const response = await jwtAxios.get(endPoints.report.payroll.mainV2, {
        params,
      });
      return {
        data: response.data.data,
        agency: response.data.agency,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getP30ReportList = createAsyncThunk<
  GetP30ReportResponse,
  GetP30ReportParams
>(
  'report/getP30ReportList',
  async (params: GetP30ReportParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.hmrc.p30p32, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getP32ReportList = createAsyncThunk<
  GetP32ReportResponse,
  GetP32ReportParams
>(
  'report/getP32ReportList',
  async (params: GetP32ReportParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.hmrc.p30p32, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getBankFilesReportList = createAsyncThunk<
  GetBankFilesReportResponse,
  GetBankFilesReportParams
>(
  'report/getGetBankFilesReportList',
  async (params: GetBankFilesReportParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.report.bankFile.main, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAttachmentOrdersReportList = createAsyncThunk<
  GetAttachmentOrdersReportListResponse,
  GetAttachmentOrdersReportListParams
>(
  'report/getAttachmentOrdersReportList',
  async (params: GetAttachmentOrdersReportListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        endPoints.report.attachmentOrder,
        {
          params,
        },
      );
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getOnHoldWorkerReportList = createAsyncThunk<
  GetOnHoldWorkerReportListResponse,
  GetOnHoldWorkerReportListParams
>(
  'report/getOnHoldWorkerReportList',
  async (params: GetOnHoldWorkerReportListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.dashboard.subcontractor, {
        params: {
          ...params,
          paymentModel: 'PAYEE',
          isOnHoldOnly: true,
        },
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const reportActs = {
  getDraftPayrollList,
  getPayrollList,
  getHolidayPayBalanceDetailList,
  getHolidayPayBalanceSummaryList,
  getNetPayList,
  getFiftyTwoWeekAverageSummaryList,
  getRTWList,
  getAWRList,
  getWTRList,
  getCISStateOfEarningsList,
  getDormantWorkerList,
  getEmploymentIntermediaryList,
  getEmploymentIntermediaryInAgencyPortalList,
  getAutoEnrollmentStatusList,
  getPensionContributionReportList,
  getPendingPensionContributionReportList,
  getP30ReportList,
  getP32ReportList,
  getBankFilesReportList,
  getAttachmentOrdersReportList,
  getOnHoldWorkerReportList,
};
